// Login functionality redirects to Deriv dashboard
// SmartTrader now uses token-based authentication (oneTimeToken -> sessionToken -> authorize)

const { getBrandSignupUrl } = require('../../../templates/_common/brand.config');
const Language = require('../language');
const dynamicConfig = require('../../config/dynamic-config');
const DerivV2Auth = require('../../services/deriv-v2-auth');
const DforgeUsage = require('../dforge_usage');

const Login = (() => {
    /**
     * Deriv OAuth 2.0 Authorization Code + PKCE login flow.
     * Reads the per-site appIdV2 from the platform-dashboard API. If present,
     * launches the v2 PKCE OAuth2 flow at auth.deriv.com via the dforge.site
     * router.
     *
     * appIdV2 is the per-site tier app from Appwrite — it MUST NOT be replaced
     * with a generic env-var default in production (that would break the tier
     * markup model). DERIV_V2_CLIENT_ID exists only as a dev convenience for
     * localhost / ngrok testing where there's no platform-dashboard record.
     */
    const isDevHost = () => {
        if (typeof window === 'undefined') return false;
        const h = window.location.hostname;
        return h === 'localhost' || h === '127.0.0.1' || h.includes('ngrok') || h.includes('devtunnels');
    };

    const getConfigForLogin = async () => {
        let config = await dynamicConfig.getDomainConfig();

        // Re-check once at login so a newly configured v2 client is visible.
        if (!isDevHost() && config && !config.appIdV2) {
            config = await dynamicConfig.getDomainConfig({ forceRefresh: true });
        }

        return config || dynamicConfig.getDomainConfigSync();
    };

    const redirectToLogin = async () => {
        const config = await getConfigForLogin();
        const appIdV2 = config && config.appIdV2;
        // Per-site appIdV2 from Appwrite is the source of truth. Env-var only
        // kicks in on dev hosts (where there's no Appwrite record at all).
        const v2ClientId = appIdV2 || (isDevHost() && process.env && process.env.DERIV_V2_CLIENT_ID) || '';

        if (v2ClientId) {
            try {
                const loginUrl = await DerivV2Auth.buildLoginURL();
                DforgeUsage.trackUsage('trader_login_started', { flow: 'v2' });
                window.location.href = loginUrl;
                return;
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[SmartTrader] Failed to build v2 login URL:', err);
                return;
            }
        } else {
            // appIdV2 is empty in production — Appwrite hasn't been configured
            // for this site yet. Surface a clear message instead of silently
            // bouncing the user to home.deriv.com via the deprecated legacy URL.
            DforgeUsage.trackUsage('trader_login_failed', { reason: 'not_configured' });
            const domain = (typeof window !== 'undefined' && window.location && window.location.hostname) || 'unknown';
            // eslint-disable-next-line no-console
            console.error(
                `[SmartTrader] appIdV2 is missing from the site config for "${domain}". ` +
                'Login cannot proceed via the v2 OAuth flow. ' +
                'Set appIdV2 on this site in the platform-dashboard (Appwrite) so the ' +
                'API returns it on /api/sites/smarttrader-config.',
                { domain, isNotConfigured: config && config.isNotConfigured, hasConfig: !!config }
            );
            return;
        }
    };

    const redirectToSignup = () => {
        // Use dynamic config signup URL if available
        if (window.SmartTraderConfig && window.SmartTraderConfig.signupUrl) {
            window.location.href = window.SmartTraderConfig.signupUrl;
            return;
        }

        const baseSignupUrl = getBrandSignupUrl();
        const lang = Language.get();
        const signupUrlWithLang = `${baseSignupUrl}?lang=${lang}`;

        window.location.href = signupUrlWithLang;
    };

    const initOneAll = () => {
        // Social login is handled by the Deriv dashboard
        // This is kept for backward compatibility but does nothing
    };

    return {
        redirectToLogin,
        redirectToSignup,
        initOneAll,
    };
})();

module.exports = Login;
